import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ETICHETE_STARE_COLABORARE,
  ETICHETE_STARE_LUNA,
  ETICHETE_TIP_COLABORARE,
  STARI_COLABORARE,
  lunaDinZi,
  lunaInCuvinteColaborare,
  type StareColaborare,
} from '@b4l/shared'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { AntetPagina } from '../../components/ui/AntetPagina'
import { Button } from '../../components/ui/Button'
import { CardCifra } from '../../components/ui/CardCifra'
import { ChipuriFiltru } from '../../components/ui/ChipuriFiltru'
import { Pastila } from '../../components/ui/Pastila'
import { StareLista } from '../../components/ui/StareLista'
import { FormularColaborare } from './colaborari/FormularColaborare'
import { PanouColaborare } from './colaborari/PanouColaborare'
import { FilaRaport } from './colaborari/FilaRaport'
import { FereastraValidatori } from './colaborari/FereastraValidatori'
import {
  TON_LUNA,
  TON_STARE,
  dataRo,
  lunaDeAzi,
  lunaScurta,
  type Colaborare,
} from './colaborari/comun'

type Fila = 'registru' | 'raport'
type Filtru = 'toate' | StareColaborare

/**
 * Marketing → Colaborări (task #2516, Dorian): registrul colaborărilor cu medici, clinici,
 * farmacii, branduri; mentenanța lunară a celor pe termen lung; raportul lunar al colaborărilor.
 * E evidența lui Dorian, nu un raport obligatoriu: lunile fără înregistrare se arată neutru.
 * Fiecare își vede doar colaborările lui; adminii și validatorii le văd pe toate (regula e pe
 * server), dar doar adminii le schimbă pe ale altora.
 */
export function ColaborariPage() {
  const permissions = useAuthStore((s) => s.permissions)
  const poateScrie = permissions.includes('colaborari:write')
  const [fila, setFila] = useState<Fila>('registru')
  const [filtru, setFiltru] = useState<Filtru>('toate')
  const [responsabilId, setResponsabilId] = useState<number | null>(null)
  const [nou, setNou] = useState(false)
  const [validatori, setValidatori] = useState(false)
  const eu = useAuthStore((s) => s.user?.id)
  const [deschis, setDeschis] = useState<number | null>(null)

  const q = useQuery({
    queryKey: ['colaborari', 'lista', responsabilId],
    queryFn: async () =>
      (
        await api.get<{
          toate: boolean
          scrieTot: boolean
          valideaza: boolean
          colaborari: Colaborare[]
        }>('/colaborari', {
          params: responsabilId ? { responsabilId } : {},
        })
      ).data,
  })
  /** Vede tot: admin sau validator. */
  const admin = q.data?.toate ?? false
  const scrieTot = q.data?.scrieTot ?? false
  const valideaza = q.data?.valideaza ?? false
  const resp = useQuery({
    queryKey: ['colaborari', 'responsabili'],
    queryFn: async () =>
      (await api.get<{ responsabili: { id: number; name: string }[] }>('/colaborari/responsabili'))
        .data.responsabili,
    enabled: admin,
  })
  const responsabili = resp.data ?? []

  const toate = useMemo(() => q.data?.colaborari ?? [], [q.data])
  const acum = lunaDeAzi()
  const cifre = useMemo(
    () => ({
      active: toate.filter((c) => c.stare === 'activ').length,
      negociere: toate.filter((c) => c.stare === 'negociere').length,
      noiLunaAsta: toate.filter((c) => c.dataSemnare && lunaDinZi(c.dataSemnare) === acum).length,
    }),
    [toate, acum],
  )
  const vizibile = toate.filter((c) => (filtru === 'toate' ? true : c.stare === filtru))

  return (
    <div className="grid gap-4">
      <AntetPagina
        titlu="Colaborări"
        descriere="Medici, clinici, farmacii și branduri cu care avem contract, cu mentenanța lunară a fiecăruia."
        explicatie="La colaborările pe termen lung poți nota, lună de lună, ce s-a făcut („cu activitate”) sau că n-a fost nimic. O lună în care n-ai notat nimic apare „fără înregistrare” și se socotește fără activitate. O lună validată nu se mai schimbă."
        actiuni={
          poateScrie && fila === 'registru' ? (
            <div className="flex gap-2">
              {scrieTot && (
                <Button variant="secondary" onClick={() => setValidatori(true)}>
                  Cine validează
                </Button>
              )}
              <Button onClick={() => setNou(true)}>+ Colaborare nouă</Button>
            </div>
          ) : undefined
        }
      >
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded border border-b4l-border p-0.5 text-sm">
            {(['registru', 'raport'] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFila(f)}
                className={`rounded px-3 py-1 ${fila === f ? 'bg-b4l-blue text-white' : 'text-b4l-text-muted hover:bg-b4l-bg'}`}
              >
                {f === 'registru' ? 'Registru' : 'Raport lunar'}
              </button>
            ))}
          </div>
          {admin && (
            <select
              value={responsabilId ?? ''}
              onChange={(e) => setResponsabilId(e.target.value ? Number(e.target.value) : null)}
              className="rounded border border-b4l-border bg-b4l-surface px-3 py-1.5 text-sm"
            >
              <option value="">Toți responsabilii</option>
              {responsabili.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </AntetPagina>

      {fila === 'raport' ? (
        <FilaRaport valideaza={valideaza} responsabilId={responsabilId} onDeschide={setDeschis} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <CardCifra ton="verde" eticheta="Active" valoare={cifre.active} />
            <CardCifra ton="albastru" eticheta="În negociere" valoare={cifre.negociere} />
            <CardCifra
              ton="mov"
              eticheta={`Semnate în ${lunaInCuvinteColaborare(acum)}`}
              valoare={cifre.noiLunaAsta}
            />
          </div>

          <ChipuriFiltru<Filtru>
            ales={filtru}
            onAlege={setFiltru}
            eticheta="Starea colaborării"
            chipuri={[
              { cheie: 'toate', eticheta: 'Toate', numar: toate.length },
              ...STARI_COLABORARE.map((s) => ({
                cheie: s as Filtru,
                eticheta: ETICHETE_STARE_COLABORARE[s],
                numar: toate.filter((c) => c.stare === s).length,
              })),
            ]}
          />

          <StareLista
            interogare={q}
            gol={vizibile.length === 0}
            mesajGol={
              toate.length === 0
                ? poateScrie
                  ? 'Nicio colaborare încă. Apasă „+ Colaborare nouă".'
                  : 'Nicio colaborare încă.'
                : 'Nicio colaborare pe filtrul ales.'
            }
          >
            <div className="overflow-x-auto rounded-caseta border border-b4l-border bg-b4l-surface">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                    <th className="px-3 py-2 font-medium">Colaborare</th>
                    <th className="px-3 py-2 font-medium">Stare</th>
                    <th className="px-3 py-2 font-medium">Semnat</th>
                    <th className="px-3 py-2 font-medium">Perioada</th>
                    <th className="px-3 py-2 font-medium">Ultimele luni</th>
                    {admin && <th className="px-3 py-2 font-medium">Responsabil</th>}
                  </tr>
                </thead>
                <tbody>
                  {vizibile.map((c) => (
                    <tr
                      key={c.id}
                      onClick={() => setDeschis(c.id)}
                      className="cursor-pointer border-b border-b4l-border last:border-0 hover:bg-b4l-bg"
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium">{c.denumire}</div>
                        <div className="text-xs text-b4l-text-muted">
                          {ETICHETE_TIP_COLABORARE[c.tip]}
                          {c.durata === 'o_data' && ' · o singură dată'}
                          {c.specialitate && ` · ${c.specialitate}`}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <Pastila ton={TON_STARE[c.stare]}>
                          {ETICHETE_STARE_COLABORARE[c.stare]}
                        </Pastila>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{dataRo(c.dataSemnare)}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs">
                        {dataRo(c.valabilDe)} –{' '}
                        {c.valabilPana ? dataRo(c.valabilPana) : 'nedeterminată'}
                      </td>
                      <td className="px-3 py-2">
                        {c.durata === 'continua' ? (
                          <div className="flex flex-wrap gap-1">
                            {c.luni.slice(-6).map((l) => (
                              <span key={l.luna} title={ETICHETE_STARE_LUNA[l.stare]}>
                                <Pastila ton={TON_LUNA[l.stare]}>{lunaScurta(l.luna)}</Pastila>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-b4l-text-muted">—</span>
                        )}
                      </td>
                      {admin && <td className="px-3 py-2 text-xs">{c.responsabil.name}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </StareLista>
        </>
      )}

      {nou && (
        <FormularColaborare
          responsabili={scrieTot ? responsabili : []}
          onInchide={(c) => {
            setNou(false)
            if (c) setDeschis(c.id)
          }}
        />
      )}
      {validatori && <FereastraValidatori onInchide={() => setValidatori(false)} />}
      {deschis != null && (
        <PanouColaborare
          id={deschis}
          poateScrie={poateScrie}
          scrieTot={scrieTot}
          eu={eu}
          responsabili={scrieTot ? responsabili : []}
          onInchide={() => setDeschis(null)}
        />
      )}
    </div>
  )
}
